import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    BrowserRouter as Router,
    Switch,
    Route,
    Redirect,
    Link,
    useLocation,
    useHistory,
    useRouteMatch
} from 'react-router-dom';
import { Container, NavItem, Nav, Navbar, NavLink, UncontrolledTooltip } from 'reactstrap';
import BootboxContainer from 'react-modal-promise';
import { PusherProvider } from '@harelpls/use-pusher';
import { ToastContainer, Flip } from 'react-toastify';
import { QueryClientProvider, QueryClient } from 'react-query';
import clsx from 'clsx';

import Reports from 'components/Report/Report';
import Dashboard from 'components/Dashboard/DashboardV2';
import Frontdesk from 'components/Frontdesk/Frontdesk';
import Contracts from 'components/ContractList/ReactContracts';
import Help from 'components/Help/Help';
import Metadata from 'components/MetadataView/Metadata';
import Agenda from 'components/Agenda/Agenda';
import Risks from 'components/Risks/Risks';
import Workflows from 'components/Workflows/Workflows';
import AuditView from 'components/Audit/Audit';
import SignView from 'components/Sign/SignView';
import { WordOnline } from 'components/Editor/Canvas/WordFormat';
import Editor from 'components/Editor/Editor';
import Milestones from 'components/Milestones/Milestones';
import RedirectView from 'components/Redirect/Redirect';
import Records from 'components/Records/Records';
import ApiDocs from 'components/ApiDocs/ApiDocs';
import Templates from 'components/Templates/Templates';
import Profile from 'components/Profile/Profile';
import Login from 'components/Auth/Login';
import Logout from 'components/Auth/Logout';
import UserFormList from 'components/Forms/UserFormList';
import ForgotPassword from 'components/Auth/ForgotPassword';
import IPFS from 'components/IPFS/IPFS';
import DesignSystem from 'components/DesignSystem/DesignSystem';
import ChangePassword from 'components/Auth/ChangePassword';
import { AuthProvider, useAuth } from 'context/auth';
import bootbox from 'components/Modals/Bootbox';
import { pusherConfig, pusherAuthorizer } from 'config/pusher';
import { ProfileProvider, useProfileAbility } from 'context/profile';
import MedicalPrescriptions from './MedicalPrescriptions/MedicalPrescriptions';

const queryClient = new QueryClient();

function MenuLogo() {
    const { t } = useTranslation();
    return (
        <NavItem className="mb-2">
            <UncontrolledTooltip placement="right" target={'profile'}>{t('profile.menu.configure')}</UncontrolledTooltip>
            <NavLink tag="div" id="profile">
                <Link to={'/profile'}>
                    <img
                        src="/images/owl-logo.svg"
                        height="35px"
                        type="image/svg+xml"
                        alt="logo"
                    />
                </Link>
            </NavLink>
        </NavItem>
    )
}

function MenuItem({ menu, title, id }) {
    const location = useLocation();
    const history = useHistory();
    const active = location.pathname.includes(menu.href);
    const handleClick = async () => {
        if (menu.action === 'logout') {
            if (await bootbox.confirm({ textT: 'dashboard.confirm_logout' })) {
                history.push('/logout');
            }
        } else {
            history.push(menu.href);
        }
    }
    return (
        <div id={id} onClick={handleClick}>
            <UncontrolledTooltip placement="right" target={id}>{title}</UncontrolledTooltip>
            <NavItem >
                <NavLink className={clsx({ active })}>
                    {menu.icon ?
                        <i className={clsx(menu.icon)} style={{ color: '#b0bec5 ' }}></i> :
                        <img src={menu.img} alt={title} width="30px" />
                    }
                </NavLink>
            </NavItem>
        </div>
    )
}

export const menus = [
    { name: 'dashboard', title: 'Dashboard', href: '/dashboard', img: '/images/sidebar/dashboard.png', ability: 'VIEW_DASHBOARD' },
    { name: 'records', title: 'records.my_records', href: '/records/list', img: '/images/sidebar/catalog.png', ability: 'VIEW_RECORDS' },
    { name: 'frontdesk', title: 'frontDesk.title', href: '/frontdesk', img: '/images/sidebar/frontdesk.png', ability: 'VIEW_FRONTDESK' },
    { name: 'contracts', title: 'contracts.title', href: '/contracts/list', img: '/images/sidebar/contract.png', ability: 'VIEW_CONTRACTS_LIST' },
    { name: 'reports', title: 'reports.title', href: '/reports', img: '/images/sidebar/reports.png', ability: 'VIEW_REPORTS_2' },
    { name: 'agenda', title: 'agenda.title', href: '/agenda', img: '/images/sidebar/agenda.svg', ability: 'VIEW_AGENDA' },
    { name: 'incoming-contracts', title: 'incoming.title', href: '/incoming-contracts', img: '/images/sidebar/received.png', ability: 'VIEW_INCOMING_CONTRACTS' },
    { name: 'templates', title: 'templates.title', href: '/templates/list', img: '/images/sidebar/template.png', ability: 'VIEW_TEMPLATES' },
    { name: 'workflow', title: 'workflow.title', href: '/workflow', img: '/images/sidebar/workflow.png', ability: 'VIEW_WORKFLOW' },
    { name: 'user-forms', title: 'Mis formularios', href: '/user-forms', icon: 'fas fa-clipboard-list fa-2x', ability: 'VIEW_USER_FORMS' },
    { name: 'risks', title: 'risks.title', href: '/risks', img: '/images/sidebar/risks.png', ability: 'VIEW_RISKS' },
    { name: 'blockchain', title: 'blockchain.title', href: '/blockchain', img: '/images/sidebar/blockchain.png', ability: 'VIEW_BLOCKCHAIN' },
    { name: 'metadata', title: 'metadata.title', href: '/metadata', icon: 'fas fa-search-plus fa-2x', ability: 'VIEW_METADATA_SEARCH' },
    { name: 'help', title: 'help.title', href: '/help', img: '/images/sidebar/manual.svg', ability: 'VIEW_MANUAL' },
    { name: 'exit', title: 'common.exit', action: 'logout', href: '#', icon: 'fas fa-sign-out-alt fa-2x' },
];

export function SideBar() {
    const ability = useProfileAbility()
    const { t } = useTranslation()

    return (
        <Navbar className="position-fixed sidebar-left text-center h-100 bg-white shadow m-0 p-0">
            <Nav>
                <MenuLogo />
                {menus.map((menu) => (ability.can(menu.ability) || !menu.ability)
                    && <MenuItem menu={menu} key={menu.name} title={t(menu.title)} id={menu.name} />)
                }
            </Nav>
        </Navbar>
    )
}

function TopBar() {
    return (

        <nav className="d-none navbar navbar-dark sticky-top bg-light flex-md-nowrap p-0 shadow">
            <a className="navbar-brand col-md-3 col-lg-2 mr-0 px-3" href="#">Trato</a>
            <button className="navbar-toggler position-absolute d-md-none collapsed" type="button" data-toggle="collapse" data-target="#sidebarMenu" aria-controls="sidebarMenu" aria-expanded="false" aria-label="Toggle navigation">
                <span className="navbar-toggler-icon">

                </span>
            </button>
            {/* <ul className="navbar-nav px-3">
                <li className="nav-item text-nowrap">
                    <a className="nav-link" href="#">Sign out</a>
                </li>
            </ul> */}
        </nav>

    )
}

function MainLayout({ privilege, children, noPadding }) {
    return (
        <PusherProvider {...pusherConfig} authorizer={pusherAuthorizer}>
            <ProfileProvider privilege={privilege}>
                <SideBar />
                <Container fluid className={clsx('main-container h-100', noPadding && 'p-0')}>
                    {children}
                </Container>
            </ProfileProvider>
        </PusherProvider>
    )
}

function Main() {
    return (
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
                <ToastContainer transition={Flip} />
                <BootboxContainer />
                <Router>
                    <Switch>

                        <Route path={['/audit/verify/:contractId/:jwt', '/audit/verify/:contractId', '/nom/verify']}>
                            <AuditView />
                        </Route>

                        <Route path={['/sign/:contractId/:uuid', '/sign/:contractId']}>
                            <SignView />
                        </Route>

                        <Route path='/milestones/:contractId/:milestoneId/:uuid'>
                            <Milestones />
                        </Route>

                        <Route path={['/contracts/:contractId/medical-prescriptions/:medicalPrescriptionId/:uuid', '/contracts/:contractId/medical-prescriptions/:medicalPrescriptionId']}>
                            <MedicalPrescriptions />
                        </Route>

                        <Route path='/r/:url'>
                            <RedirectView />
                        </Route>

                        <Route path='/api/docs'>
                            <ApiDocs />
                        </Route>

                        <Route path='/wordonline/:contractId'>
                            <WordOnline />
                        </Route>

                        <Route path='/logout'>
                            <Logout />
                        </Route>

                        <Route path='/account/:username/change-password/:code'>
                            <ChangePassword />
                        </Route>
                        
                        <Route path='/login/recover'>
                            <ForgotPassword />
                        </Route>
                       
                        <Route path={['/login', '/auth/redirect/:token', '/slack/oauth']}>
                            <Login />
                        </Route>

                        

                        <PrivateRoute path='/profile'>
                            <MainLayout>
                                <Profile />
                            </MainLayout>
                        </PrivateRoute>

                        <PrivateRoute path='/dashboard' >
                            <MainLayout privilege="VIEW_DASHBOARD">
                                <Dashboard />
                            </MainLayout>
                        </PrivateRoute>

                        <PrivateRoute path={['/frontdesk/contract/:contractId', '/frontdesk/template/:templateId', '/frontdesk']} >
                            <MainLayout privilege='VIEW_FRONTDESK'>
                                <Frontdesk />
                            </MainLayout>
                        </PrivateRoute>

                        <PrivateRoute path={['/records/list/contracts/:recordId', '/contracts/list/:childOf', '/contracts/list']}>
                            <MainLayout>
                                <Contracts />
                            </MainLayout>
                        </PrivateRoute>

                        <PrivateRoute path={['/records/list', '/records/list/:recordId']} >
                            <MainLayout privilege="VIEW_RECORDS">
                                <Records />
                            </MainLayout>
                        </PrivateRoute>

                        <PrivateRoute path='/search-contracts/:searchTerm'>
                            <MainLayout>
                                <Contracts />
                            </MainLayout>
                        </PrivateRoute>

                        <PrivateRoute path='/user-forms'>
                            <MainLayout privilege="VIEW_USER_FORMS">
                                <UserFormList />
                            </MainLayout>
                        </PrivateRoute>

                        <PrivateRoute path='/reports' >
                            <MainLayout privilege="VIEW_REPORTS_2">
                                <Reports />
                            </MainLayout>
                        </PrivateRoute>

                        <PrivateRoute path='/agenda' >
                            <MainLayout privilege="VIEW_AGENDA">
                                <Agenda />
                            </MainLayout>
                        </PrivateRoute>

                        <PrivateRoute path='/incoming-contracts' >
                            <MainLayout privilege="VIEW_INCOMING_CONTRACTS">
                                <Contracts />
                            </MainLayout>
                        </PrivateRoute>

                        <PrivateRoute path='/contracts/editor/:documentId' >
                            <MainLayout noPadding privilege="VIEW_EDITOR">
                                <Editor mode="contract" />
                            </MainLayout>
                        </PrivateRoute>

                        <PrivateRoute path='/templates/editor/:documentId'>
                            <MainLayout noPadding privilege="VIEW_TEMPLATES">
                                <Editor mode='template' />
                            </MainLayout>
                        </PrivateRoute>

                        <PrivateRoute path='/templates/list' >
                            <MainLayout privilege="VIEW_TEMPLATES">
                                <Templates />
                            </MainLayout>
                        </PrivateRoute>

                        <PrivateRoute path='/workflow' >
                            <MainLayout privilege="VIEW_WORKFLOW">
                                <Workflows />
                            </MainLayout>
                        </PrivateRoute>

                        <PrivateRoute path='/risks' >
                            <MainLayout privilege="VIEW_RISKS">
                                <Risks />
                            </MainLayout>
                        </PrivateRoute>

                        <PrivateRoute path={['/blockchain/:txId', '/blockchain']} >
                            <MainLayout noPadding privilege="VIEW_BLOCKCHAIN">
                                <IPFS />
                            </MainLayout>
                        </PrivateRoute>

                        <PrivateRoute path='/metadata' >
                            <MainLayout privilege="VIEW_METADATA_SEARCH"><Metadata /></MainLayout>
                        </PrivateRoute>

                        <PrivateRoute path='/help'>
                            <MainLayout privilege='VIEW_MANUAL'><Help /></MainLayout>
                        </PrivateRoute>

                        <PrivateRoute path='/design'>
                            <DesignSystem />
                        </PrivateRoute>

                        <Redirect path="/" to='/dashboard'>
                        </Redirect>
                    </Switch>

                </Router>
            </AuthProvider>
        </QueryClientProvider>
    )
}

function PrivateRoute({ children, ...rest }) {
    const { auth } = useAuth();

    return (
        <Route
            {...rest}
            render={({ location }) =>
                auth?.accessToken ? (
                    children
                ) : (
                    <Redirect
                        to={{
                            pathname: '/login',
                            state: { from: location }
                        }}
                    />
                )
            }
        />
    );
}

export default Main;